import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Card, CardContent, CircularProgress } from '@mui/material';
import { isAuthenticated, login, msalInstance } from '../auth/authProvider';
import { createSessionCookies } from '../helpers/helpers';
import { BSWButton, BSWLogo, TranslationHubLogo } from '../components';
import { setUser } from '../store/slices/authSlice';
import { useAppDispatch } from '../hooks/redux';
import { ErrorOutline } from '@mui/icons-material';

const LoginScreen: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Single function to handle successful authentication
  const handleSuccessfulAuth = useCallback(() => {
    // Create session cookies on successful authentication
    createSessionCookies();
    dispatch(setUser());
    const from = location.state?.from?.pathname || '/translations';
    navigate(from, { replace: true });
  }, [dispatch, location.state, navigate]);

  // Handle redirect promise and check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Handle redirect response from Azure AD
        const response = await msalInstance.handleRedirectPromise();
        
        if ((response && response.account) || isAuthenticated()) {
          // User just logged in via redirect OR User was already authenticated
          handleSuccessfulAuth();
        } else {
          // not authenticated yet — show login button
        }

      } catch (error) {
        console.error('Error handling authentication:', error);
        setAuthError('Login failed. Try again later.');
      } finally {
        setAuthChecking(false);
      }
    };

    checkAuth();
  }, [handleSuccessfulAuth]);

  const handleLogin = async () => {
    setLoading(true);
    setAuthError(null);
    try {
      await login();
      // After login redirect, this will be handled by the redirect callback
    } catch (error) {
      console.error('Login failed:', error);
      setAuthError("Unable to start login. Please try again.");
      setLoading(false);
    }
  };

  // Show loading while checking auth status
  if (authChecking) {
    return (
      <Box sx={styles.mainContainer}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={48} />
          <div>Checking authentication...</div>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box component="header" sx={styles.header}>
        <BSWLogo />
      </Box>

      <Box sx={styles.mainContainer}>
        <Card elevation={0} sx={styles.card}>
          <CardContent sx={styles.cardContent}>
            <Box sx={{ mb: 2 }}>
              <TranslationHubLogo translationIconSize={67}/>
            </Box>
            {authError && <Alert icon={<ErrorOutline />} severity="error" style={styles.errorAlert}>
              {authError}
            </Alert>}
            {/* Login Button */}
            <BSWButton loading={loading} loadingText="Logging in..." onClick={handleLogin} sx={styles.loginButton}>
              LOGIN WITH SSO
            </BSWButton>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

// Styles object (unchanged)
const styles = {
  header: {
    position: 'fixed' as const,
    top: 20,
    left: 16,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none' as const,
    zIndex: 1000,
  },
  mainContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#FAFAFA',
  },
  card: {
    width: 440,
    borderRadius: 2,
    overflow: 'hidden',
    background: '#ffffff',
    border: '1px solid rgba(0, 0, 0, 0.15)',
    textAlign: 'center',
    p: 2,
  },
  cardContent: {
    pt: 2,
    px: 4,
    pb: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginButton: {
    width: 375,
    height: 42,
    fontSize: '1rem',
    textTransform: 'none',
    display: 'block',
  },
  errorAlert: {
    marginBottom: 24,
    width: 375,
  }
};

export default LoginScreen;
